// Befehlspalette (Cmd/Strg+K oder „/“): Leads und Kontakte suchen (Name, Firma, Telefon, E-Mail),
// zuletzt geöffnete Leads, Befehle der Seite, Navigation und Smart Views.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sucheApi, type SuchTreffer } from "../api";
import { aktivitaetenApi, type AktivitaetSuchTreffer } from "../api/aktivitaeten";
import { artIcon } from "../aktivitaeten/arten";
import { useAuth } from "../auth/AuthContext";
import { useMeta } from "../daten/meta";
import { useBefehle, type Befehl } from "../daten/seite";
import { useZuletzt } from "../daten/zuletzt";
import { T } from "../texte";
import { ebene } from "../werkzeuge/tasten";
import { useWorkflowMenue } from "../workflows";
import { StatusPille } from "./Bausteine";
import { fehlerInfo } from "./Fehler";
import { Icon, type IconName } from "./Icon";
import { leadsImportierenOeffnen, neuerLeadOeffnen } from "./smartview/dialogBus";

interface Eintrag {
  schluessel: string;
  gruppe: string;
  icon: IconName;
  titel: string;
  unter?: string | null;
  status?: string | null;
  taste?: string;
  aktion: () => void;
}

/** Unterseiten der Berichte (Route, Name wie im Reiter) */
const BERICHT_SEITEN: [string, string][] = [
  ["vergleich", "Aktivitätsvergleich"],
  ["trichter", "Opportunity-Trichter"],
  ["anrufe", "Anrufe"],
  ["statuswechsel", "Statuswechsel"],
  ["gesendet", "Gesendete E-Mails"],
];

const normal = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

const MATCH: Record<SuchTreffer["match"], string> = { name: "Name", company: "Firma", phone: "Telefon", email: "E-Mail" };

export function Befehlspalette({ offen, schliessen, onTasten }: { offen: boolean; schliessen: () => void; onTasten: () => void }) {
  const [q, setQ] = useState("");
  const [treffer, setTreffer] = useState<SuchTreffer[]>([]);
  // Gruppe „Aktivitäten“ (S07): Treffer in Notizen, Mails, SMS, Anrufnotizen und Custom Activities, höchstens 5
  const [aktivTreffer, setAktivTreffer] = useState<AktivitaetSuchTreffer[]>([]);
  const [sucht, setSucht] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState(0);
  const eingabe = useRef<HTMLInputElement>(null);
  const liste = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const { meta } = useMeta();
  const { nutzer } = useAuth();
  const kannWorkflows = !!nutzer?.permissions.includes("workflows_verwalten");
  const kannTelefonie = !!nutzer?.permissions.includes("telefonie_verwalten");
  const seitenBefehle = useBefehle();
  const zuletzt = useZuletzt(offen);
  // „Workflow auslösen für diesen Lead“: nur auf einer Lead-Seite; der Dialog lebt außerhalb der Palette und bleibt nach dem Schließen offen
  const ort = useLocation();
  const aktuellerLead = /^\/lead\/([^/]+)/.exec(ort.pathname);
  const aktuelleLeadId = aktuellerLead ? decodeURIComponent(aktuellerLead[1]) : "";
  const workflowMenue = useWorkflowMenue({ art: "lead", leadId: aktuelleLeadId, leadName: zuletzt.find((l) => l.id === aktuelleLeadId)?.name ?? "Lead" });

  useEffect(() => {
    if (!offen) return;
    ebene.auf();
    setQ("");
    setTreffer([]);
    setAktivTreffer([]);
    setMeldung(null);
    setAuswahl(0);
    window.setTimeout(() => eingabe.current?.focus(), 0);
    return () => ebene.zu();
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    const text = q.trim();
    if (text.length < 2) {
      setTreffer([]);
      setAktivTreffer([]);
      setSucht(false);
      setMeldung(null);
      return;
    }
    const abbruch = new AbortController();
    setSucht(true);
    const zeit = window.setTimeout(() => {
      aktivitaetenApi
        .suche(text, 5, abbruch.signal)
        .then((r) => setAktivTreffer(r.data))
        .catch(() => setAktivTreffer([]));
      sucheApi
        .suchen(text, 12, abbruch.signal)
        .then((r) => {
          setTreffer(r.data);
          setMeldung(null);
          setSucht(false);
        })
        .catch((e) => {
          if ((e as Error).name === "AbortError") return;
          setTreffer([]);
          setSucht(false);
          setMeldung(fehlerInfo(e).text);
        });
    }, 120);
    return () => {
      abbruch.abort();
      window.clearTimeout(zeit);
    };
  }, [q, offen]);

  const zu = (pfad: string) => () => {
    schliessen();
    navigate(pfad);
  };

  const befehle: Befehl[] = useMemo(() => {
    const allgemein: Befehl[] = [
      { id: "nav-inbox", label: "Zur Inbox", icon: "inbox", gruppe: "Navigation", aktion: () => navigate("/inbox") },
      { id: "nav-leads", label: "Zu allen Leads", icon: "leads", gruppe: "Navigation", aktion: () => navigate("/leads") },
      { id: "nav-kontakte", label: "Kontakte", icon: "kontakte", gruppe: "Navigation", aktion: () => navigate("/kontakte") },
      { id: "nav-aktivitaeten", label: "Aktivitäten", icon: "aktivitaet", gruppe: "Navigation", aktion: () => navigate("/aktivitaeten") },
      { id: "nav-opportunities", label: "Opportunities", icon: "opportunity", gruppe: "Navigation", aktion: () => navigate("/opportunities") },
      { id: "nav-berichte", label: "Berichte", icon: "ansicht", gruppe: "Navigation", aktion: () => navigate("/reports") },
      ...BERICHT_SEITEN.map(([pfad, name]) => ({ id: `nav-bericht-${pfad}`, label: `Zu Berichte > ${name}`, icon: "ansicht" as const, gruppe: "Navigation", aktion: () => navigate(`/reports/${pfad}`) })),
      { id: "nav-gespraeche-verlauf", label: "Zu Gespräche > Verlauf", icon: "telefon", gruppe: "Navigation", aktion: () => navigate("/gespraeche") },
      { id: "nav-gespraeche-live", label: "Zu Gespräche > Live", icon: "telefon", gruppe: "Navigation", aktion: () => navigate("/gespraeche/live") },
      ...(kannTelefonie ? [{ id: "nav-gespraeche-qualitaet", label: "Zu Gespräche > Qualität", icon: "telefon" as const, gruppe: "Navigation", aktion: () => navigate("/gespraeche/qualitaet") }] : []),
      { id: "lead-neu", label: "Neuen Lead anlegen", icon: "plus", taste: "⌘⇧L", gruppe: "Navigation", aktion: () => neuerLeadOeffnen() },
      { id: "lead-import", label: "Leads importieren (CSV)", icon: "plus", gruppe: "Navigation", aktion: () => leadsImportierenOeffnen() },
      { id: "nav-views", label: "Alle Smart Views", icon: "felder", gruppe: "Navigation", aktion: () => navigate("/smart-views/alle") },
      { id: "nav-views-geteilt", label: "Geteilte Smart Views", icon: "link", gruppe: "Navigation", aktion: () => navigate("/smart-views/geteilt") },
      { id: "nav-views-andere", label: "Smart Views von anderen", icon: "ansicht", gruppe: "Navigation", aktion: () => navigate("/smart-views/andere") },
      { id: "nav-workflows", label: "Workflows", icon: "blitz", gruppe: "Navigation", aktion: () => navigate("/workflows") },
      { id: "nav-workflow-laeufe", label: "Workflow-Läufe", icon: "blitz", gruppe: "Navigation", aktion: () => navigate("/workflows/laeufe") },
      ...(kannWorkflows ? [{ id: "workflow-neu", label: "Neuer Workflow", icon: "blitz" as const, gruppe: "Navigation", aktion: () => navigate("/workflows/neu") }] : []),
      { id: "nav-einst", label: "Einstellungen öffnen", icon: "einstellungen", gruppe: "Navigation", aktion: () => navigate("/einstellungen") },
      { id: "tasten", label: "Tastenkürzel anzeigen", icon: "tastatur", taste: "?", gruppe: "Navigation", aktion: onTasten },
    ];
    const views: Befehl[] = (meta?.smart_views ?? []).map((v) => ({
      id: `view-${v.id}`,
      label: `Smart View: ${v.name}`,
      icon: "ansicht",
      gruppe: "Smart Views",
      aktion: () => navigate(`/smart-views/${v.id}`),
    }));
    const ausloesen = workflowMenue.punkte[0];
    const leadBefehle: Befehl[] =
      aktuelleLeadId && kannWorkflows && ausloesen?.onClick
        ? [{ id: "lead-workflow-ausloesen", label: "Workflow auslösen für diesen Lead", icon: "blitz", gruppe: "Diese Seite", aktion: () => ausloesen.onClick?.() }]
        : [];
    return [...seitenBefehle.map((b) => ({ ...b, gruppe: b.gruppe ?? "Diese Seite" })), ...leadBefehle, ...allgemein, ...views];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, seitenBefehle, navigate, onTasten, kannWorkflows, kannTelefonie, aktuelleLeadId]);

  const eintraege: Eintrag[] = useMemo(() => {
    const text = q.trim();
    const n = normal(text);
    const aus: Eintrag[] = [];
    if (!text) {
      for (const l of zuletzt) {
        aus.push({
          schluessel: `z-${l.id}`,
          gruppe: "Zuletzt geöffnet",
          icon: "leads",
          titel: l.name,
          unter: l.kontakt,
          status: l.status_label,
          aktion: zu(`/lead/${encodeURIComponent(l.id)}${l.kontakt_id ? `?kontakt=${encodeURIComponent(l.kontakt_id)}` : ""}`),
        });
      }
    } else {
      for (const t of treffer) {
        aus.push({
          schluessel: `${t.type}-${t.contact_id ?? t.lead_id}-${t.match}`,
          gruppe: t.type === "lead" ? "Leads" : "Kontakte",
          icon: t.type === "lead" ? "leads" : "person",
          titel: t.title,
          unter: [t.subtitle, t.match !== "name" && t.match_value ? `${MATCH[t.match]}: ${t.match_value}` : null].filter(Boolean).join(" · "),
          status: t.type === "contact" ? t.status_label : null,
          aktion: zu(`/lead/${t.lead_id}${t.contact_id ? `?kontakt=${encodeURIComponent(t.contact_id)}` : ""}`),
        });
      }
    }
    for (const a of aktivTreffer) {
      if (!a.lead_id) continue;
      aus.push({
        schluessel: `akt-${a.id}`,
        gruppe: "Aktivitäten",
        icon: artIcon(a.art),
        titel: a.titel ?? "Aktivität",
        unter: [a.lead_name, a.ausschnitt].filter(Boolean).join(" · "),
        aktion: zu(`/lead/${encodeURIComponent(a.lead_id)}?aktivitaet=${encodeURIComponent(a.id)}`),
      });
    }
    const passende = n ? befehle.filter((b) => normal(b.label).includes(n)) : befehle.filter((b) => b.gruppe !== "Smart Views" || !text);
    for (const b of passende.slice(0, text ? 12 : 40)) {
      aus.push({
        schluessel: `b-${b.id}`,
        gruppe: b.gruppe ?? "Befehle",
        icon: b.icon ?? "pfeil-rechts",
        titel: b.label,
        taste: b.taste,
        aktion: () => {
          schliessen();
          b.aktion();
        },
      });
    }
    return aus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, treffer, aktivTreffer, befehle, zuletzt]);

  useEffect(() => {
    setAuswahl(0);
  }, [treffer, aktivTreffer, q]);

  useEffect(() => {
    liste.current?.querySelector<HTMLElement>(`[data-index="${auswahl}"]`)?.scrollIntoView({ block: "nearest" });
  }, [auswahl]);

  if (!offen) return <>{workflowMenue.dialoge}</>;

  let letzteGruppe = "";
  return (
    <>
    {workflowMenue.dialoge}
    <div className="palette-hintergrund" onMouseDown={schliessen} role="presentation">
      <div className="palette" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label={T.suche.kurz}>
        <div className="palette__kopf">
          <Icon name="suche" groesse={18} />
          <input
            ref={eingabe}
            className="palette__eingabe"
            placeholder={T.suche.platzhalter}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                schliessen();
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setAuswahl((a) => Math.min(a + 1, eintraege.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setAuswahl((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                eintraege[auswahl]?.aktion();
              }
            }}
          />
          {sucht ? <span className="kreisel kreisel--klein" aria-label="Sucht" /> : null}
        </div>
        {meldung ? <div className="palette__leer palette__leer--fehler">{meldung}</div> : null}
        {q.trim().length === 1 ? <div className="palette__leer">{T.suche.mindestens}</div> : null}
        {q.trim().length >= 2 && !sucht && !meldung && treffer.length === 0 && aktivTreffer.length === 0 ? <div className="palette__leer">Keine Leads, Kontakte oder Aktivitäten gefunden.</div> : null}
        <ul ref={liste} className="palette__liste" role="listbox">
          {eintraege.map((e, i) => {
            const kopf = e.gruppe !== letzteGruppe ? e.gruppe : null;
            letzteGruppe = e.gruppe;
            return (
              <li key={e.schluessel} role="presentation">
                {kopf ? <div className="palette__gruppe">{kopf}</div> : null}
                <div
                  data-index={i}
                  className="palette__treffer"
                  role="option"
                  aria-selected={i === auswahl}
                  onMouseMove={() => setAuswahl(i)}
                  onClick={() => e.aktion()}
                >
                  <Icon name={e.icon} groesse={16} className="palette__icon" />
                  <span className="palette__texte">
                    <span className="palette__titel">{e.titel}</span>
                    {e.unter ? <span className="palette__unter">{e.unter}</span> : null}
                  </span>
                  {e.status ? <StatusPille label={e.status} farbe={meta?.lead_statuses.find((s) => s.label === e.status)?.color} klein /> : null}
                  {e.taste ? <kbd>{e.taste}</kbd> : null}
                  {i === auswahl ? <span className="palette__enter">↵</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="palette__fuss">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> auswählen
          </span>
          <span>
            <kbd>↵</kbd> öffnen
          </span>
          <span>
            <kbd>Esc</kbd> schließen
          </span>
        </div>
      </div>
    </div>
    </>
  );
}
